import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ExtensionInstallChrome() {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  const openLightbox = (imageSrc: string) => {
    setLightboxImage(imageSrc);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative">
            <img 
              src={lightboxImage}
              alt="Enlarged view"
              className="w-[720px] h-[480px] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={closeLightbox}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            How to Install Chrome Extension Manually
          </h1>
          <p className="text-lg text-gray-600">
            Follow these simple steps to manually install the XeroVerifier extension in Google Chrome
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-6">
            {/* Step 1 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform scale-75 origin-top">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold text-lg">1</span>
                </div>
                <h2 className="text-2xl font-bold text-white">Download the Extension File</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-700 mb-4 text-lg">
                      Click the download button to get the extension ZIP file to your computer.
                    </p>
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                      <div className="flex items-start gap-2">
                        <i className="ri-information-line text-blue-600 text-lg mt-0.5"></i>
                        <div>
                          <p className="font-semibold text-blue-900 text-sm">Tip</p>
                          <p className="text-blue-800 text-sm">Remember where you save the file - you'll need to find it in the next step.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <div 
                      className="cursor-zoom-in"
                      onClick={() => openLightbox("https://readdy.ai/api/search-image?query=Clean%20modern%20user%20interface%20showing%20a%20prominent%20blue%20download%20button%20with%20downward%20arrow%20icon%2C%20minimalist%20design%2C%20white%20background%2C%20simple%20and%20clear%20call-to-action%20button%2C%20professional%20software%20interface%2C%20high%20contrast%2C%20centered%20composition&width=720&height=480&seq=download-button-ui-lg&orientation=landscape")}
                    >
                      <img 
                        src="https://readdy.ai/api/search-image?query=Clean%20modern%20user%20interface%20showing%20a%20prominent%20blue%20download%20button%20with%20downward%20arrow%20icon%2C%20minimalist%20design%2C%20white%20background%2C%20simple%20and%20clear%20call-to-action%20button%2C%20professional%20software%20interface%2C%20high%20contrast%2C%20centered%20composition&width=720&height=480&seq=download-button-ui-lg&orientation=landscape"
                        alt="Download button interface"
                        className="w-[270px] h-[180px] object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform scale-75 origin-top">
              <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 font-bold text-lg">2</span>
                </div>
                <h2 className="text-2xl font-bold text-white">Extract the ZIP File</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-700 mb-4">
                      Locate the downloaded ZIP file and extract it to a folder on your computer.
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-green-500 mt-1"></i>
                        <span>Right-click the ZIP file</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-green-500 mt-1"></i>
                        <span>Select "Extract All..." or "Extract Here"</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-green-500 mt-1"></i>
                        <span>Choose a location you can easily find</span>
                      </li>
                    </ul>
                  </div>
                  <div className="flex items-center justify-center">
                    <div 
                      className="cursor-zoom-in"
                      onClick={() => openLightbox("https://readdy.ai/api/search-image?query=Computer%20desktop%20screenshot%20showing%20Windows%20file%20explorer%20with%20a%20ZIP%20file%20selected%20and%20right-click%20context%20menu%20open%20displaying%20Extract%20All%20option%2C%20realistic%20operating%20system%20interface%2C%20clear%20and%20professional%2C%20modern%20Windows%20interface%20design&width=720&height=480&seq=zip-extract-menu-lg&orientation=landscape")}
                    >
                      <img 
                        src="https://readdy.ai/api/search-image?query=Computer%20desktop%20screenshot%20showing%20Windows%20file%20explorer%20with%20a%20ZIP%20file%20selected%20and%20right-click%20context%20menu%20open%20displaying%20Extract%20All%20option%2C%20realistic%20operating%20system%20interface%2C%20clear%20and%20professional%2C%20modern%20Windows%20interface%20design&width=720&height=480&seq=zip-extract-menu-lg&orientation=landscape"
                        alt="ZIP file extraction menu"
                        className="w-[270px] h-[180px] object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform scale-75 origin-top">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-600 font-bold text-lg">3</span>
                </div>
                <h2 className="text-2xl font-bold text-white">Open Chrome Extensions Page</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-700 mb-4">
                      Open Google Chrome and navigate to the Extensions management page.
                    </p>
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded mb-4">
                      <p className="font-semibold text-purple-900 text-sm mb-2">Three ways to get there:</p>
                      <ul className="space-y-1 text-purple-800 text-sm">
                        <li>• Click the three dots menu → Extensions → Manage Extensions</li>
                        <li>• Type <code className="bg-purple-100 px-2 py-0.5 rounded">chrome://extensions</code> in the address bar</li>
                        <li>• Use keyboard shortcut: <code className="bg-purple-100 px-2 py-0.5 rounded">Ctrl+Shift+E</code> (Windows) or <code className="bg-purple-100 px-2 py-0.5 rounded">Cmd+Shift+E</code> (Mac)</li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <div 
                      className="cursor-zoom-in"
                      onClick={() => openLightbox("https://readdy.ai/api/search-image?query=Google%20Chrome%20browser%20interface%20showing%20the%20three-dot%20menu%20opened%20with%20Extensions%20submenu%20highlighted%2C%20clean%20browser%20screenshot%2C%20modern%20Chrome%20interface%2C%20professional%20and%20clear%20UI%20design%2C%20realistic%20browser%20window&width=720&height=480&seq=chrome-extensions-menu-lg&orientation=landscape")}
                    >
                      <img 
                        src="https://readdy.ai/api/search-image?query=Google%20Chrome%20browser%20interface%20showing%20the%20three-dot%20menu%20opened%20with%20Extensions%20submenu%20highlighted%2C%20clean%20browser%20screenshot%2C%20modern%20Chrome%20interface%2C%20professional%20and%20clear%20UI%20design%2C%20realistic%20browser%20window&width=720&height=480&seq=chrome-extensions-menu-lg&orientation=landscape"
                        alt="Chrome extensions menu"
                        className="w-[270px] h-[180px] object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform scale-75 origin-top">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-orange-600 font-bold text-lg">4</span>
                </div>
                <h2 className="text-2xl font-bold text-white">Enable Developer Mode</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-700 mb-4">
                      In the top-right corner of the Extensions page, toggle on "Developer mode".
                    </p>
                    <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded">
                      <div className="flex items-start gap-2">
                        <i className="ri-alert-line text-orange-600 text-lg mt-0.5"></i>
                        <div>
                          <p className="font-semibold text-orange-900 text-sm">Important</p>
                          <p className="text-orange-800 text-sm">Developer mode must be enabled to install unpacked extensions. This is safe and normal for manual installations.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <div 
                      className="cursor-zoom-in"
                      onClick={() => openLightbox("https://readdy.ai/api/search-image?query=Chrome%20extensions%20settings%20page%20showing%20Developer%20mode%20toggle%20switch%20in%20the%20ON%20position%2C%20located%20in%20top%20right%20corner%2C%20clean%20Chrome%20interface%2C%20professional%20browser%20screenshot%2C%20modern%20UI%20design%2C%20clear%20and%20focused&width=720&height=480&seq=developer-mode-toggle-lg&orientation=landscape")}
                    >
                      <img 
                        src="https://readdy.ai/api/search-image?query=Chrome%20extensions%20settings%20page%20showing%20Developer%20mode%20toggle%20switch%20in%20the%20ON%20position%2C%20located%20in%20top%20right%20corner%2C%20clean%20Chrome%20interface%2C%20professional%20browser%20screenshot%2C%20modern%20UI%20design%2C%20clear%20and%20focused&width=720&height=480&seq=developer-mode-toggle-lg&orientation=landscape"
                        alt="Developer mode toggle"
                        className="w-[270px] h-[180px] object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform scale-75 origin-top">
              <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold text-lg">5</span>
                </div>
                <h2 className="text-2xl font-bold text-white">Load Unpacked Extension</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-700 mb-4">
                      Click "Load unpacked" and select the folder where you extracted the extension files.
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-teal-500 mt-1"></i>
                        <span>Click the "Load unpacked" button</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-teal-500 mt-1"></i>
                        <span>Navigate to the extracted folder</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-teal-500 mt-1"></i>
                        <span>Select the folder and click "Select Folder"</span>
                      </li>
                    </ul>
                  </div>
                  <div className="flex items-center justify-center">
                    <div 
                      className="cursor-zoom-in"
                      onClick={() => openLightbox("https://readdy.ai/api/search-image?query=Chrome%20extensions%20page%20showing%20Load%20unpacked%20button%20with%20file%20browser%20dialog%20open%2C%20selecting%20extension%20folder%2C%20professional%20Chrome%20interface%20screenshot%2C%20modern%20browser%20UI%2C%20clear%20and%20instructional&width=720&height=480&seq=load-unpacked-button-lg&orientation=landscape")}
                    >
                      <img 
                        src="https://readdy.ai/api/search-image?query=Chrome%20extensions%20page%20showing%20Load%20unpacked%20button%20with%20file%20browser%20dialog%20open%2C%20selecting%20extension%20folder%2C%20professional%20Chrome%20interface%20screenshot%2C%20modern%20browser%20UI%2C%20clear%20and%20instructional&width=720&height=480&seq=load-unpacked-button-lg&orientation=landscape"
                        alt="Load unpacked button and file browser"
                        className="w-[270px] h-[180px] object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 6 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform scale-75 origin-top">
              <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-indigo-600 font-bold text-lg">6</span>
                </div>
                <h2 className="text-2xl font-bold text-white">Verify Installation</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-700 mb-4">
                      The extension should now appear in your Chrome toolbar. You're all set!
                    </p>
                    <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                      <div className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-green-600 text-lg mt-0.5"></i>
                        <div>
                          <p className="font-semibold text-green-900 text-sm">Success!</p>
                          <p className="text-green-800 text-sm">You should see the XeroVerifier icon in your Chrome toolbar. Click it to start using the extension.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <div 
                      className="cursor-zoom-in"
                      onClick={() => openLightbox("https://readdy.ai/api/search-image?query=Chrome%20browser%20toolbar%20showing%20newly%20installed%20extension%20icon%2C%20clean%20browser%20interface%2C%20extension%20icon%20visible%20in%20toolbar%20area%2C%20professional%20Chrome%20screenshot%2C%20modern%20UI%20design%2C%20successful%20installation%20indicator&width=720&height=480&seq=extension-installed-toolbar-lg&orientation=landscape")}
                    >
                      <img 
                        src="https://readdy.ai/api/search-image?query=Chrome%20browser%20toolbar%20showing%20newly%20installed%20extension%20icon%2C%20clean%20browser%20interface%2C%20extension%20icon%20visible%20in%20toolbar%20area%2C%20professional%20Chrome%20screenshot%2C%20modern%20UI%20design%2C%20successful%20installation%20indicator&width=720&height=480&seq=extension-installed-toolbar-lg&orientation=landscape"
                        alt="Extension installed in toolbar"
                        className="w-[270px] h-[180px] object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Troubleshooting Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Troubleshooting Common Issues
            </h2>
            <p className="text-xl text-gray-600">
              Having problems? Here are solutions to the most common installation issues.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-red-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Error: "Manifest file is missing"</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    This means you selected the wrong folder. Make sure you select the folder that contains 
                    the manifest.json file, not the parent folder.
                  </p>
                  <p className="text-sm text-gray-700 font-medium">
                    Solution: Go back to Step 5 and select the correct folder.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-red-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Extension icon not showing</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    The extension might be hidden. Click the puzzle piece icon in Chrome's toolbar to see all extensions.
                  </p>
                  <p className="text-sm text-gray-700 font-medium">
                    Solution: Pin the extension by clicking the pin icon next to it.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-red-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Can't find Developer mode toggle</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Make sure you're on the chrome://extensions/ page, not the Chrome Web Store.
                  </p>
                  <p className="text-sm text-gray-700 font-medium">
                    Solution: Type chrome://extensions/ directly in the address bar.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-red-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Extension keeps getting disabled</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Chrome may disable extensions from unknown sources. This is normal for manually installed extensions.
                  </p>
                  <p className="text-sm text-gray-700 font-medium">
                    Solution: Click "Enable" when prompted, or keep Developer mode ON.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Need Help Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-customer-service-2-line text-white text-3xl"></i>
          </div>
          <h2 className="text-4xl font-bold mb-6">
            Still Need Help?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Our support team is here to help you with any installation issues. 
            We typically respond within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap cursor-pointer inline-block"
            >
              <i className="ri-mail-line mr-2"></i>
              Contact Support
            </a>
            <a 
              href="/how-it-works"
              className="bg-white/10 text-white border-2 border-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer inline-block"
            >
              <i className="ri-book-open-line mr-2"></i>
              View Documentation
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}